import { useState } from 'react';
import { useLoginLog } from '../../hooks/useLoginLog';
import { DataTable, type Column } from '../../components/DataTable';
import type { LoginEventDto, QueryOptions } from '../../lib/types';

// Matches the run-status pills on the Ingest page so "good / bad" reads the same across admin.
const OUTCOME_PILL: Record<LoginEventDto['outcome'], string> = {
  Success: 'bg-emerald-100 text-emerald-700',
  Failure: 'bg-red-100 text-red-700',
};

export function LoginLogPage() {
  const [options, setOptions] = useState<QueryOptions>({
    page: 1,
    pageSize: 25,
    sortBy: 'occurredUtc',
    sortDesc: true,
  });
  const { data, isLoading, error } = useLoginLog(options);

  const columns: Column<LoginEventDto>[] = [
    {
      header: 'When',
      sortBy: 'occurredUtc',
      render: (e) => new Date(e.occurredUtc).toLocaleString(),
      cellClass: 'whitespace-nowrap',
    },
    {
      header: 'User',
      sortBy: 'email',
      render: (e) => <span className="text-brand-charcoal">{e.email}</span>,
    },
    {
      header: 'Outcome',
      sortBy: 'outcome',
      render: (e) => (
        <span className={`text-xs px-2 py-0.5 rounded-full ${OUTCOME_PILL[e.outcome]}`}>
          {e.outcome}
        </span>
      ),
    },
    {
      header: 'Reason',
      render: (e) =>
        e.failureReason ? (
          <span className="text-xs text-red-700">{e.failureReason}</span>
        ) : (
          <span className="text-slate-400">—</span>
        ),
    },
    {
      header: 'Provider',
      sortBy: 'provider',
      render: (e) => (
        <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-600">{e.provider}</span>
      ),
    },
    {
      header: 'IP',
      render: (e) => <span className="font-mono text-xs">{e.ipAddress ?? '—'}</span>,
      cellClass: 'whitespace-nowrap',
    },
    {
      header: 'Device',
      // User agents are long and low-value at a glance; the full string is on hover.
      render: (e) =>
        e.userAgent ? (
          <span className="text-xs text-brand-charcoal/60" title={e.userAgent}>
            {e.userAgent.slice(0, 40)}
            {e.userAgent.length > 40 ? '…' : ''}
          </span>
        ) : (
          <span className="text-slate-400">—</span>
        ),
    },
  ];

  return (
    <section className="bg-white rounded-lg shadow p-6 space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-brand-blue">Login Log</h1>
        <p className="text-sm text-brand-charcoal/70">
          Every sign-in attempt, successful or not. Read-only and admin-only.
        </p>
      </div>

      <DataTable
        data={data}
        isLoading={isLoading}
        error={error}
        options={options}
        onOptionsChange={setOptions}
        columns={columns}
        rowKey={(e) => e.id}
        searchable
        searchPlaceholder="Search by email or IP…"
        emptyState="No sign-in attempts recorded yet."
      />
    </section>
  );
}
